/**
 * ClientFB_19: 画像表示の統一フェードイン
 *
 * クライアント FB「初回読み込み時に画像が断続的・バラバラにカットインする。
 * 最初のアクセスからフェードインしながら順番に表示したい」への対応。
 *
 * 方針:
 *  - ネットワーク取得順は一切変更しない（lazy / below_fold / fetchpriority は既存のまま）。
 *    変えるのは「表示のしかた」だけ: 未ロードの画像を透明にしておき、
 *    load → decode 完了後に 0.45s の opacity フェードで表示する。
 *  - ロード完了は DOM 順に近い順序で並ぶため、結果として「上から順にフェード」して見える。
 *    直列ダウンロードや人工的な待機は行わない。
 *
 * 対象: <main> 内の loading="lazy" の <img> のみ。
 *  - eager / auto（loading 属性なし）の画像は LCP 候補（コレクション MV・上段カード・
 *    NEWS 一覧の先頭記事画像・商品ギャラリー 1 枚目など）のため対象外
 *    （実測: NEWS 一覧で auto 画像を対象にすると LCP が約 +460ms 悪化した）。
 *  - [fetchpriority="high"] / TOP FV 画像（専用のスクロールフェードが opacity を管理）/
 *    スプラッシュ内の画像（ロゴ複製）も明示的に除外。
 *
 * 安全設計:
 *  - prefers-reduced-motion: reduce では何もしない（即時表示）。
 *  - 非表示用 CSS はこのスクリプトが注入する。JS が動かない環境では CSS 自体が
 *    存在しないため、画像は従来どおり表示される（真っ白事故が起きない）。
 *  - キャッシュ済みで既に complete な画像には触らない（warm 表示を遅らせない）。
 *  - load / error のどちらでも必ず表示に戻す。
 *  - <head> 内で同期実行し、パース中に追加される <img> へ MutationObserver で
 *    ロード完了前にクラスを付与する（defer では初期表示画像のロードに間に合わない）。
 *  - variant 切替（product-rerender）等で後から挿入される画像にも同じ処理が効く。
 */
(function () {
  'use strict';

  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
  } catch (error) {
    /* matchMedia が使えない環境では従来表示のまま */
    return;
  }

  var WAIT_CLASS = 'emission-imgfade-wait';
  var IN_CLASS = 'emission-imgfade-in';

  var style = document.createElement('style');
  style.textContent =
    'img.' + WAIT_CLASS + '{opacity:0}' +
    'img.' + IN_CLASS + '{animation:emission-imgfade .45s ease both}' +
    '@keyframes emission-imgfade{from{opacity:0}to{opacity:1}}';

  /* 実行タイミングによっては head / documentElement が未生成のことがあるため遅延挿入 */
  function ensureStyle() {
    if (style.parentNode) return;
    var parent = document.head || document.documentElement;
    if (parent) parent.appendChild(style);
  }
  ensureStyle();

  function isTarget(img) {
    if (img.tagName !== 'IMG') return false;
    if (img.loading !== 'lazy') return false;
    if (img.getAttribute('fetchpriority') === 'high') return false;
    if (img.classList.contains('top-prism-section__fv-image')) return false;
    if (img.closest && img.closest('[data-top-prism-splash]')) return false;
    if (img.closest && !img.closest('main')) return false;
    return true;
  }

  var seen = new WeakSet();

  function attach(img) {
    if (seen.has(img)) return;
    seen.add(img);
    if (!isTarget(img)) return;

    /* 既にロード済み（キャッシュ等）の画像はそのまま表示（warm を遅らせない） */
    if (img.complete && img.naturalWidth > 0) return;

    img.classList.add(WAIT_CLASS);

    var reveal = function () {
      img.classList.remove(WAIT_CLASS);
      img.classList.add(IN_CLASS);
    };

    img.addEventListener(
      'load',
      function () {
        /* decode 完了後にフェード開始（デコード中の白ちらつきを防ぐ） */
        if (img.decode) {
          img.decode().then(reveal, reveal);
        } else {
          reveal();
        }
      },
      { once: true }
    );

    /* 読み込み失敗時も隠したままにしない（alt テキストを表示） */
    img.addEventListener(
      'error',
      function () {
        img.classList.remove(WAIT_CLASS);
      },
      { once: true }
    );
  }

  function scan(root) {
    if (!root.querySelectorAll) return;
    var imgs = root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) attach(imgs[i]);
  }

  new MutationObserver(function (mutations) {
    ensureStyle();
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'IMG') attach(node);
        else scan(node);
      }
    }
  }).observe(document, { childList: true, subtree: true });

  ensureStyle();
  scan(document);
})();
